import type { AccountantReportView } from 'shared-models/browser';
import { isolateAmountLtr } from '../../../../utils/money';
import type { DateRange } from './accountant-report.types';

const pad = (n: number): string => String(n).padStart(2, '0');

// yyyy-mm-dd for a local date (no timezone shift).
export function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// Default range: first day -> last day of the month containing `now`.
export function currentMonthRange(now: Date = new Date()): DateRange {
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: toIsoDate(first), to: toIsoDate(last) };
}

// dd/mm/yyyy from an ISO timestamp; falls back to the raw string when unparseable.
export function formatReportDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return iso;
  }
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

// 1,234.50 — 2-decimal thousands-grouped money for the preview cells.
export function formatMoney(value: number): string {
  return value.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Minimal HTML escaping for values injected into the standalone print document.
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Builds a fully self-contained RTL HTML document for the accountant report.
// Deliberately framework-agnostic (no Mantine / CSS-module dependency) so the
// printed PDF renders identically to the on-screen table regardless of styles
// that would otherwise be lost when printing from a detached window.
export function buildAccountantReportPrintHtml(view: AccountantReportView): string {
  const { businessName, cardBrands, rows, totals } = view;
  const num = (value: number): string => isolateAmountLtr(escapeHtml(formatMoney(value)));

  const brandHeadCells = cardBrands.map((brand) => `<th class="num">${escapeHtml(brand)}</th>`).join('');
  const paymentColSpan = 5 + cardBrands.length + 1;

  const bodyRows = rows
    .map((row) => {
      const brandCells = cardBrands.map((brand) => `<td class="num">${num(row.byBrand[brand] ?? 0)}</td>`).join('');
      return `<tr>
        <td class="text">${escapeHtml(formatReportDate(row.date))}</td>
        <td class="text">${escapeHtml(row.sessionName)}</td>
        <td class="num">${num(row.net)}</td>
        <td class="num">${num(row.vat)}</td>
        <td class="num">${num(row.gross)}</td>
        <td class="num">${num(row.cash)}</td>
        <td class="num">${num(row.checks)}</td>
        <td class="num">${num(row.onAccount)}</td>
        <td class="num">${num(row.bankTransfer)}</td>
        ${brandCells}
        <td class="num">${num(row.other)}</td>
        <td class="num">${num(row.totalIncome)}</td>
        <td class="num">${num(row.tips)}</td>
      </tr>`;
    })
    .join('');

  const emptyRow = `<tr><td class="empty" colspan="${6 + paymentColSpan}">אין משמרות בטווח שנבחר</td></tr>`;

  const brandTotals = cardBrands.map((brand) => `<th class="num">${num(totals.byBrand[brand] ?? 0)}</th>`).join('');
  const totalsRow = `<tr class="totals">
    <th class="text">סה"כ</th>
    <th class="text"></th>
    <th class="num">${num(totals.net)}</th>
    <th class="num">${num(totals.vat)}</th>
    <th class="num">${num(totals.gross)}</th>
    <th class="num">${num(totals.cash)}</th>
    <th class="num">${num(totals.checks)}</th>
    <th class="num">${num(totals.onAccount)}</th>
    <th class="num">${num(totals.bankTransfer)}</th>
    ${brandTotals}
    <th class="num">${num(totals.other)}</th>
    <th class="num">${num(totals.totalIncome)}</th>
    <th class="num">${num(totals.tips)}</th>
  </tr>`;

  const title = `דו"ח רואה חשבון — ${escapeHtml(businessName)}`;

  return `<!doctype html>
<html lang="he" dir="rtl">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${title}</title>
    <style>
      * { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; background: #fff; color: #1f2933;
        font-family: 'Segoe UI', Arial, sans-serif; direction: rtl; }
      .host { padding: 16px; }
      h1 { font-size: 18px; font-weight: 700; margin: 0 0 12px; text-align: right; }
      table { width: 100%; border-collapse: collapse; font-size: 11px; }
      th, td { border: 1px solid #c7ccd1; padding: 4px 6px; white-space: nowrap; }
      thead th { background: #eef1f4; font-weight: 700; }
      th.group { text-align: center; }
      td.text, th.text { text-align: right; }
      td.num, th.num { text-align: center; font-variant-numeric: tabular-nums; }
      tbody tr:nth-child(even) td { background: #f7f8fa; }
      tfoot .totals th { background: #eef1f4; font-weight: 700; }
      td.empty { text-align: center; color: #6b7280; padding: 16px; }
      @page { size: landscape; margin: 8mm; }
      @media print {
        .host { padding: 0; }
        thead { display: table-header-group; }
        tfoot { display: table-footer-group; }
        tr { break-inside: avoid; }
      }
    </style>
  </head>
  <body>
    <div class="host">
      <h1>${title}</h1>
      <table>
        <thead>
          <tr>
            <th class="text" rowspan="2">תאריך</th>
            <th class="text" rowspan="2">מספר Z</th>
            <th class="group" colspan="3">סך הכל מכירות</th>
            <th class="group" colspan="${paymentColSpan}">סוגי תשלום</th>
            <th class="num" rowspan="2">עודף טיפ</th>
          </tr>
          <tr>
            <th class="num">מכירות נטו</th>
            <th class="num">מע"מ</th>
            <th class="num">סה"כ מכירות</th>
            <th class="num">מזומן</th>
            <th class="num">המחאות</th>
            <th class="num">לקוח קיים</th>
            <th class="num">העברה בנקאית</th>
            ${brandHeadCells}
            <th class="num">אחר</th>
            <th class="num">סה"כ הכנסות</th>
          </tr>
        </thead>
        <tbody>
          ${rows.length > 0 ? bodyRows : emptyRow}
        </tbody>
        ${rows.length > 0 ? `<tfoot>${totalsRow}</tfoot>` : ''}
      </table>
    </div>
  </body>
</html>`;
}
